import * as React from 'react';

import classNames from 'classnames';
import { format } from 'date-fns';
import { NavLink, withRouter } from 'react-router-dom';

import { clientConfig } from 'config/client';
import { htmlSubstr } from 'core/helpers/htmlSubstr';
import { calculateNewUrl } from 'core/i18n/locale';
import { FadeTransition } from 'components/form/components/transition/FadeTransition';

import { ArticleFooter } from './components/ArticleFooter';
import { IProps, IState } from './interfaces';

import styles from './Article.module.styl';
import { Dialog } from '@material-ui/core';

const CONTENT_PREVIEW_MAX_SYMBOLS_COUNT = 200;

class Article extends React.Component<IProps, IState> {
  public state = {
    alt: null,
    isLoaded: false,
    lightbox: null,
  };

  public componentDidMount(): void {
    window.addEventListener('message', this.onMessage);
  }

  public componentWillUnmount(): void {
    window.removeEventListener('message', this.onMessage);
  }

  render() {
    const {
      data: { article: initialArticle },
      preview,
      match,
    } = this.props;

    const {
      params: { locale },
    } = match;

    const article =
      locale && initialArticle.translation && initialArticle.translation[locale]
        ? {
            ...initialArticle,
            ...initialArticle.translation[locale],
          }
        : initialArticle;

    const content = preview
      ? article.preview ||
        htmlSubstr(article.content, CONTENT_PREVIEW_MAX_SYMBOLS_COUNT, false)
      : article.preview
      ? article.preview + article.content
      : article.content;

    const readMore =
      preview && (article.preview || content.length < article.content.length);

    const widgetsMap = article.widgets
      ? article.widgets.reduce((acc: any, item: any) => {
          acc[item.name] = item.code;
          return acc;
        }, {})
      : {};

    const contentWithWidgets = content.replace(
      /&lt;WIDGET:([a-z0-9]+)&gt;/gi,
      (a: string, widgetKey: string) => {
        let widgetCode = widgetsMap[widgetKey];

        if (!widgetCode) {
          return '';
        }

        const id: string = `widget-${article.id}-${widgetKey}`;

        return `<iframe id="${id}" class="widget-container" src="${clientConfig.apiPath}/files/download/widget/${article.id}/${widgetKey}"></iframe>`;
      },
    );

    return (
      <div
        className={styles.root}
        onClick={this.handleClick}
        aria-hidden="true"
      >
        <div
          className={
            preview || article.categoryId ? styles.header : styles.bigHeader
          }
          dangerouslySetInnerHTML={{
            __html: article.title,
          }}
        />
        {!readMore && article.featuredMedia && (
          <img
            className={styles.featuredMedia}
            src={article.featuredMedia}
            title={article.title}
            alt={article.title}
          />
        )}
        <div
          className={classNames(styles.content, {
            [styles.withReadmore]: readMore && !article.preview,
          })}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: contentWithWidgets,
          }}
        />
        {readMore && (
          <NavLink
            className={styles.readMore}
            to={calculateNewUrl(
              match.params.locale,
              `${
                article.publishDate
                  ? format(new Date(article.publishDate), 'yyyy/MM/dd')
                  : ''
              }/${article.slug}`,
            )}
          >
            Read More
          </NavLink>
        )}
        <ArticleFooter
          categories={article.categories}
          hashtags={article.hashtags}
          publishDate={article.publishDate ? article.publishDate : null}
          lastUpdatedDate={
            article.lastUpdatedDate ? article.lastUpdatedDate : null
          }
        />
        <Dialog
          fullScreen={false}
          open={Boolean(this.state.lightbox)}
          onClose={this.closeDialog}
          TransitionComponent={FadeTransition}
          scroll="body"
          maxWidth={false}
          onClick={this.closeDialog}
        >
          <div className={styles.lightboxWrapper}>
            <img
              src={this.state.lightbox || ''}
              alt={this.state.alt || ''}
              onLoad={this.setLoaded}
              className={
                this.state.isLoaded ? styles.isLoaded : styles.isLoading
              }
            />
          </div>
        </Dialog>
      </div>
    );
  }

  private readonly closeDialog = () => {
    this.setState({
      alt: null,
      isLoaded: false,
      lightbox: null,
    });
  };

  private readonly handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLDivElement;

    if (target.getAttribute('data-lightbox') !== 'on') {
      return;
    }

    this.setState({
      alt: target.getAttribute('alt'),
      lightbox: target.getAttribute('src'),
    });
  };

  private readonly onMessage = (event: MessageEvent) => {
    if (event.data.load) {
      const el = window.document.getElementById(event.data.load.id);

      if (el) {
        // @ts-ignore
        el.height = event.data.load.height;
      }
    }
  };

  private readonly setLoaded = () => {
    this.setState({
      isLoaded: true,
    });
  };
}

export default withRouter(Article);
